import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from '@/components/LocalizedLink';
import { ArrowRight, MapPin, Package, CheckCircle, Shield, Globe } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { SafeHtml } from '@/components/SafeHtml';
import { useLanguage } from '@/hooks/useLanguage';
import { useApiData } from '@/hooks/useApiData';

const NO_ITEMS = [];
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const PROSE = 'max-w-4xl mx-auto prose prose-earth prose-headings:font-display prose-headings:text-earth-dark prose-p:text-earth/70 prose-p:font-sans prose-a:text-saffron';

export default function LandingPage() {
  const { slug } = useParams();
  const pageReq = useApiData(`/api/landing-pages/${slug}`, null);
  const { data: products, loading: productsLoading } = useApiData('/api/products', NO_ITEMS);
  const { t } = useLanguage();
  const page = pageReq.data;
  const notFound = Boolean(pageReq.error);

  useEffect(() => {
    if (notFound) axios.post('/api/log-404', { path: `/market/${slug}`, referrer: document.referrer }).catch(() => {});
  }, [notFound, slug]);

  const pageProducts = useMemo(() => {
    if (!page) return NO_ITEMS;
    return page.products?.length > 0 ? products.filter(p => page.products.includes(p.id)) : products.slice(0, 4);
  }, [page, products]);

  if (pageReq.loading || productsLoading) return <div className="min-h-screen pt-32 flex items-center justify-center"><div className="animate-pulse text-earth/30 font-sans">Loading...</div></div>;
  if (!page) return <div className="min-h-screen pt-32 flex flex-col items-center justify-center gap-4"><p className="text-earth/50 font-sans text-lg">Page not found</p><Link to="/" className="text-saffron font-sans font-semibold text-sm">Back to Home</Link></div>;

  return (
    <div data-testid="landing-page">

      {/* Hero */}
      <section className="relative pt-28 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-earth-dark to-earth-dark/95 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23D4870A" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <div className="flex items-center gap-3 mb-4">
              <MapPin size={18} className="text-saffron" />
              <span className="text-saffron font-sans font-semibold text-sm uppercase tracking-wider">{page.country}</span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight max-w-3xl" data-testid="landing-h1">
              {page.h1 || `Indian Spices for ${page.country}`}
            </h1>
            <p className="mt-6 text-lg text-cream-warm/70 font-sans max-w-2xl leading-relaxed">
              {page.intro}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to={`/contact?source=${slug}`} className="inline-flex items-center gap-2 bg-saffron text-white font-sans font-semibold text-sm px-8 py-4 rounded-md hover:bg-saffron-dark transition-colors shadow-saffron-glow" data-testid="landing-cta">
                {page.cta_text || t.hero.cta1} <ArrowRight size={16} />
              </Link>
              <a href="https://wa.me/919284548218" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border-2 border-white/30 text-white font-sans font-semibold text-sm px-8 py-4 rounded-md hover:bg-white/10 transition-colors">
                WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="bg-saffron/5 py-6 px-4 border-b border-saffron/10">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-center gap-6 sm:gap-12">
          {[
            { icon: Shield, text: 'FSSAI Certified' },
            { icon: CheckCircle, text: 'APEDA Registered' },
            { icon: Globe, text: '15+ Countries' },
            { icon: Package, text: 'MOQ 1 MT' },
          ].map(b => (
            <div key={b.text} className="flex items-center gap-2 text-earth/60 font-sans text-sm">
              <b.icon size={16} className="text-saffron" /> {b.text}
            </div>
          ))}
        </div>
      </section>

      {/* Content */}
      {page.content && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <SafeHtml html={page.content} className={PROSE} testId="landing-content" />
        </section>
      )}

      {/* Products for this market */}
      {pageProducts.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-cream/30">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-display text-2xl font-bold text-earth-dark mb-8">Products We Export to {page.country}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              {pageProducts.map(p => (
                <Link key={p.id} to={`/products/${p.id}`} className="group bg-white rounded-lg overflow-hidden shadow-card-lift hover:shadow-card-hover transition-all border border-saffron-100/50" data-testid={`landing-product-${p.id}`}>
                  {p.image ? (
                    <div className="aspect-[4/3] overflow-hidden"><img src={p.image} alt={`${p.name} exporter ${page.country}`} width={800} height={600} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" /></div>
                  ) : (
                    <div className="aspect-[4/3] bg-gradient-to-br from-saffron-50/60 to-cream flex items-center justify-center"><Package size={28} className="text-earth/20" /></div>
                  )}
                  <div className="p-4">
                    <h3 className="font-display text-base font-bold text-earth-dark group-hover:text-saffron transition-colors">{p.name}</h3>
                    <p className="text-xs text-earth/50 font-sans mt-0.5">{p.tagline}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-earth-dark">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-3xl font-bold text-white">{t.cta.title1} {page.country} {t.cta.title2}?</h2>
          <p className="mt-4 text-cream-muted/70 font-sans">{t.cta.desc}</p>
          <div className="mt-8 flex justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-saffron text-white font-sans font-semibold text-sm px-8 py-4 rounded-md hover:bg-saffron-dark transition-colors shadow-saffron-glow">
              {page.cta_text || t.cta.quote} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
